/**
 * streamPool — singleton LRU para mantener WebRTC PeerConnections + MediaStreams
 * activos en memoria entre montajes de componentes WhepPlayer.
 *
 * Sprint Performance Live 2026-06-21.
 *
 * Problema que resuelve: cada vez que un operador hace switch de cámara en
 * MapView Q1 o arrastra a una cell del MonitorView, el WhepPlayer monta de
 * cero, negocia ICE (500-1500ms), DTLS (200ms), espera primer keyframe
 * (500-2000ms). Total: 1.5-3s perdidos.
 *
 * Con el pool, las últimas N cámaras vistas quedan con su PC activo en
 * background. Cuando el operador vuelve a una de ellas, el switch es
 * <100ms (solo cambia srcObject del nuevo <video>).
 *
 * Estrategia:
 *   - LRU eviction al exceder MAX_SIZE.
 *   - touch(key) marca como reciente al usar.
 *   - release(key) solo "marca released" — el PC sigue activo hasta que
 *     el pool decide cerrarlo por LRU.
 *   - get(key) retorna solo si el PC está en estado healthy (connected o
 *     completed). Si está failed/closed/disconnected, retorna null y el
 *     caller crea uno nuevo.
 *
 * Estado del pool:
 *   Map<key, PoolEntry>
 *     key      : `${siteId}-${channel}` (whep) — distingue por cámara
 *     entry    : { pc, stream, close, lastUsed, refCount }
 *       pc       : RTCPeerConnection activa
 *       stream   : MediaStream con video+audio del remoto
 *       close    : función que cierra PC y libera recursos
 *       lastUsed : timestamp ms — para LRU
 *       refCount : cuántos componentes la están usando ahora (típicamente 0 o 1)
 */

const MAX_SIZE = 4   // ajustable, ver task #379 tuning

const pool = new Map()   // key → entry
let _id = 0              // contador para logs

function _log(...args) {
  if (typeof window !== 'undefined' && window.__STREAM_POOL_DEBUG) {
    console.log('[streamPool]', ...args)
  }
}

function _isHealthy(pc) {
  if (!pc) return false
  const s = pc.connectionState
  return s === 'connected' || s === 'completed'
}

function _evictLRU() {
  // Liberar las entries con refCount=0 menos recientes hasta caer bajo MAX_SIZE
  while (pool.size > MAX_SIZE) {
    let oldestKey = null
    let oldest = Infinity
    for (const [k, e] of pool) {
      if (e.refCount === 0 && e.lastUsed < oldest) {
        oldest = e.lastUsed
        oldestKey = k
      }
    }
    if (!oldestKey) break   // todas con refCount>0 — no podemos evictar
    const e = pool.get(oldestKey)
    _log('evict LRU', oldestKey)
    try { e.close() } catch {}
    pool.delete(oldestKey)
  }
}

export const streamPool = {
  /**
   * Devuelve entry si está en pool Y healthy. Sino null.
   * Incrementa refCount + touch.
   */
  acquire(key) {
    const e = pool.get(key)
    if (!e) return null
    if (!_isHealthy(e.pc)) {
      _log('acquire rechazado por unhealthy', key, e.pc?.connectionState)
      try { e.close() } catch {}
      pool.delete(key)
      return null
    }
    e.refCount += 1
    e.lastUsed = Date.now()
    _log('acquire HIT', key, 'refCount', e.refCount)
    return { stream: e.stream, pc: e.pc }
  },

  /**
   * Registrar entry nueva en pool. Si la key ya existía, cierra la vieja.
   */
  put(key, { pc, stream, close }) {
    if (pool.has(key)) {
      const old = pool.get(key)
      try { old.close() } catch {}
      pool.delete(key)
    }
    const entry = {
      id: ++_id, pc, stream, close,
      lastUsed: Date.now(), refCount: 1,
    }
    pool.set(key, entry)
    _log('put', key, 'size', pool.size)
    _evictLRU()
    return entry
  },

  /**
   * Marcar entry como released por el componente. NO cierra — solo decrementa
   * refCount. El pool decide si la cierra por LRU eviction.
   */
  release(key) {
    const e = pool.get(key)
    if (!e) return
    e.refCount = Math.max(0, e.refCount - 1)
    e.lastUsed = Date.now()
    _log('release', key, 'refCount', e.refCount)
  },

  /**
   * Forzar cierre + remover de pool (para errores).
   */
  drop(key) {
    const e = pool.get(key)
    if (!e) return
    try { e.close() } catch {}
    pool.delete(key)
    _log('drop', key)
  },

  /**
   * Estado debug — útil en consola del browser.
   */
  stats() {
    const out = { size: pool.size, max: MAX_SIZE, entries: [] }
    for (const [k, e] of pool) {
      out.entries.push({
        key: k, refCount: e.refCount, pcState: e.pc?.connectionState,
        ageMs: Date.now() - e.lastUsed,
      })
    }
    return out
  },

  /**
   * Limpieza total — al logout o si hay leak.
   */
  clear() {
    for (const e of pool.values()) {
      try { e.close() } catch {}
    }
    pool.clear()
    _log('clear')
  },
}

// Exponer en window para debug desde DevTools console del operador
if (typeof window !== 'undefined') {
  window.__streamPool = streamPool
}
